// ╔══════════════════════════════════════════════════════════════════╗
// ║                    ADMIN USERS PAGE                              ║
// ╚══════════════════════════════════════════════════════════════════╝

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Users,
  Search,
  Filter,
  MoreVertical,
  Eye,
  Edit2,
  Ban,
  CheckCircle,
  XCircle,
  Shield,
  Mail,
  Phone,
  Calendar,
  DollarSign,
  Loader2,
  RefreshCw,
  X,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Key
} from 'lucide-react'
import { UsersAPI } from '../../services/adminApi'
import { useAdmin } from '../AdminApp'

export default function UsersPage() {
  const { hasPermission } = useAdmin()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 })
  const [filters, setFilters] = useState({ search: '', status: '', tier: '', kycStatus: '' })
  const [selectedUser, setSelectedUser] = useState(null)
  const [showUserModal, setShowUserModal] = useState(false)
  const [showActionMenu, setShowActionMenu] = useState(null)

  useEffect(() => {
    fetchUsers()
  }, [pagination.page, filters])

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      }
      const res = await UsersAPI.getAll(params)
      if (res.success) {
        setUsers(res.data || [])
        setPagination(prev => ({ ...prev, total: res.pagination?.total || 0 }))
      }
    } catch (err) {
      console.error('Failed to fetch users:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleViewUser = async (userId) => {
    setShowActionMenu(null)
    const res = await UsersAPI.getById(userId)
    if (res.success) {
      setSelectedUser(res.data)
      setShowUserModal(true)
    }
  }

  const handleToggleStatus = async (userId, newStatus) => {
    setShowActionMenu(null)
    const res = await UsersAPI.toggleStatus(userId, newStatus)
    if (res.success) {
      fetchUsers()
    }
  }

  const handleKYCAction = async (userId, status) => {
    const res = await UsersAPI.verifyKYC(userId, status)
    if (res.success) {
      fetchUsers()
      if (selectedUser?.user?.id === userId) {
        handleViewUser(userId)
      }
    }
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0)
  }

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-500/20 text-emerald-400',
      suspended: 'bg-red-500/20 text-red-400',
      inactive: 'bg-slate-500/20 text-slate-400',
      pending: 'bg-orange-500/20 text-orange-400'
    }
    return styles[status] || styles.pending
  }

  const getKYCBadge = (status) => {
    const styles = {
      approved: 'bg-emerald-500/20 text-emerald-400',
      rejected: 'bg-red-500/20 text-red-400',
      pending: 'bg-orange-500/20 text-orange-400',
      not_started: 'bg-slate-500/20 text-slate-400'
    }
    return styles[status] || styles.not_started
  }

  const totalPages = Math.ceil(pagination.total / pagination.limit)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Users className="w-7 h-7 text-blue-400" />
            Customers
          </h1>
          <p className="text-slate-400 mt-1">Manage customer accounts and profiles</p>
        </div>
        <button
          onClick={fetchUsers}
          className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="md:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
            />
          </div>
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="px-4 py-2.5 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="inactive">Inactive</option>
          </select>
          <select
            value={filters.tier}
            onChange={(e) => setFilters(prev => ({ ...prev, tier: e.target.value }))}
            className="px-4 py-2.5 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
          >
            <option value="">All Tiers</option>
            <option value="standard">Standard</option>
            <option value="silver">Silver</option>
            <option value="gold">Gold</option>
            <option value="platinum">Platinum</option>
          </select>
          <select
            value={filters.kycStatus}
            onChange={(e) => setFilters(prev => ({ ...prev, kycStatus: e.target.value }))}
            className="px-4 py-2.5 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
          >
            <option value="">All KYC Status</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700 bg-slate-900/50">
                <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Customer</th>
                <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Contact</th>
                <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Status</th>
                <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">KYC</th>
                <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Tier</th>
                <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Balance</th>
                <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Joined</th>
                <th className="text-right py-4 px-4 text-sm font-medium text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center">
                    <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto" />
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-slate-500">
                    No customers found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">
                            {user.first_name?.[0]}{user.last_name?.[0]}
                          </span>
                        </div>
                        <div>
                          <p className="text-white font-medium">{user.first_name} {user.last_name}</p>
                          <p className="text-slate-500 text-sm">{user.account_count || 0} accounts</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-slate-300 text-sm">{user.email}</p>
                      <p className="text-slate-500 text-sm">{user.phone || '-'}</p>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(user.status)}`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${getKYCBadge(user.kyc_status)}`}>
                        {user.kyc_status || 'not started'}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-slate-300 capitalize">{user.tier || 'standard'}</span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-white font-medium">{formatCurrency(user.total_balance)}</span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-slate-400 text-sm">{formatDate(user.created_at)}</span>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="relative">
                        <button
                          onClick={() => setShowActionMenu(showActionMenu === user.id ? null : user.id)}
                          className="p-2 hover:bg-slate-600 rounded-lg transition-colors"
                        >
                          <MoreVertical className="w-5 h-5 text-slate-400" />
                        </button>

                        <AnimatePresence>
                          {showActionMenu === user.id && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.95 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.95 }}
                              className="absolute right-0 mt-2 w-48 bg-slate-700 border border-slate-600 rounded-lg shadow-xl z-10"
                            >
                              <button
                                onClick={() => handleViewUser(user.id)}
                                className="w-full flex items-center gap-3 px-4 py-2 text-slate-300 hover:bg-slate-600 transition-colors"
                              >
                                <Eye className="w-4 h-4" />
                                View Details
                              </button>
                              {hasPermission('users.edit') && (
                                <button
                                  onClick={() => handleViewUser(user.id)}
                                  className="w-full flex items-center gap-3 px-4 py-2 text-slate-300 hover:bg-slate-600 transition-colors"
                                >
                                  <Edit2 className="w-4 h-4" />
                                  Edit User
                                </button>
                              )}
                              {hasPermission('users.suspend') && user.status === 'active' && (
                                <button
                                  onClick={() => handleToggleStatus(user.id, 'suspended')}
                                  className="w-full flex items-center gap-3 px-4 py-2 text-red-400 hover:bg-slate-600 transition-colors"
                                >
                                  <Ban className="w-4 h-4" />
                                  Suspend User
                                </button>
                              )}
                              {hasPermission('users.suspend') && user.status === 'suspended' && (
                                <button
                                  onClick={() => handleToggleStatus(user.id, 'active')}
                                  className="w-full flex items-center gap-3 px-4 py-2 text-emerald-400 hover:bg-slate-600 transition-colors"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                  Activate User
                                </button>
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700">
            <p className="text-sm text-slate-400">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="p-2 hover:bg-slate-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5 text-slate-400" />
              </button>
              <span className="text-slate-400 text-sm">
                Page {pagination.page} of {totalPages}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === totalPages}
                className="p-2 hover:bg-slate-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      <AnimatePresence>
        {showUserModal && selectedUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowUserModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-slate-700">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                    <span className="text-white font-bold text-xl">
                      {selectedUser.user?.first_name?.[0]}{selectedUser.user?.last_name?.[0]}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      {selectedUser.user?.first_name} {selectedUser.user?.last_name}
                    </h2>
                    <p className="text-slate-400">{selectedUser.user?.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowUserModal(false)}
                  className="p-2 hover:bg-slate-700 rounded-lg"
                >
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* User Info */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white">User Information</h3>
                    <div className="bg-slate-900/50 rounded-xl p-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Status</span>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(selectedUser.user?.status)}`}>
                          {selectedUser.user?.status}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">KYC Status</span>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getKYCBadge(selectedUser.user?.kyc_status)}`}>
                          {selectedUser.user?.kyc_status || 'not started'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Tier</span>
                        <span className="text-white capitalize">{selectedUser.user?.tier || 'standard'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Phone</span>
                        <span className="text-white">{selectedUser.user?.phone || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">2FA</span>
                        <span className={selectedUser.user?.two_factor_enabled ? 'text-emerald-400' : 'text-slate-500'}>
                          {selectedUser.user?.two_factor_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Joined</span>
                        <span className="text-white">{formatDate(selectedUser.user?.created_at)}</span>
                      </div>
                    </div>

                    {/* KYC Actions */}
                    {hasPermission('users.kyc') && selectedUser.user?.kyc_status === 'pending' && (
                      <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
                        <p className="text-orange-400 font-medium mb-3">KYC Verification Pending</p>
                        <div className="flex gap-3">
                          <button
                            onClick={() => handleKYCAction(selectedUser.user.id, 'approved')}
                            className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleKYCAction(selectedUser.user.id, 'rejected')}
                            className="flex-1 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Accounts */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white">Accounts ({selectedUser.accounts?.length || 0})</h3>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {selectedUser.accounts?.length > 0 ? (
                        selectedUser.accounts.map((account) => (
                          <div key={account.id} className="bg-slate-900/50 rounded-xl p-4 flex items-center justify-between">
                            <div>
                              <p className="text-white font-medium">{account.account_name || account.type_name}</p>
                              <p className="text-slate-500 text-sm">••{account.account_number?.slice(-4)}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-white font-semibold">{formatCurrency(account.balance)}</p>
                              <p className={`text-xs ${account.status === 'active' ? 'text-emerald-400' : 'text-slate-500'}`}>
                                {account.status}
                              </p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-slate-500 text-center py-8">No accounts</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Recent Transactions */}
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Recent Transactions</h3>
                  <div className="bg-slate-900/50 rounded-xl overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-700">
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Description</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Category</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Date</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedUser.transactions?.length > 0 ? (
                          selectedUser.transactions.slice(0, 5).map((txn) => (
                            <tr key={txn.id} className="border-b border-slate-700/50">
                              <td className="py-3 px-4 text-white">{txn.description || txn.merchant_name || '-'}</td>
                              <td className="py-3 px-4 text-slate-400">{txn.category || '-'}</td>
                              <td className="py-3 px-4 text-slate-400">{formatDate(txn.created_at)}</td>
                              <td className={`py-3 px-4 text-right font-medium ${txn.amount >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                {formatCurrency(txn.amount)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={4} className="py-8 text-center text-slate-500">No transactions</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
